/**
 * sl-opil.7.2: Credit Balance Widget for Workspace AI Settings.
 * Compact consumption-focused display: "X / Y verbraucht" + progress bar.
 * Only visible when Managed AI mode is active.
 *
 * sl-m82f.3.1: Now reusable across workspace:ai and workspace:workflow tabs.
 * The `highlightContext` prop draws a colored sub-bar inside the main "used"
 * section to highlight either AI or Workflow spend, while keeping the overall
 * total bar identical. The full breakdown (AI / Workflow / Other) lives in
 * the tooltip-style footer.
 */

import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { Button } from '@affine/component/ui/button';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { type CSSProperties, useMemo } from 'react';

import { useCreditBalance } from './use-credit-balance';
import { useManagedAIStatus } from './use-managed-ai';

// ─── Styles ──────────────────────────────────────────────────────────────────

const containerStyle: CSSProperties = {
  padding: '16px',
  backgroundColor: 'var(--streamlain-background-secondary-color)',
  borderRadius: '8px',
};

const headerRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: '10px',
};

const usageTextStyle: CSSProperties = {
  fontSize: '20px',
  fontWeight: 600,
  color: 'var(--streamlain-text-primary-color)',
  margin: 0,
};

const totalTextStyle: CSSProperties = {
  fontSize: '20px',
  fontWeight: 400,
  color: 'var(--streamlain-text-secondary-color)',
};

const progressBarBgStyle: CSSProperties = {
  width: '100%',
  height: '8px',
  backgroundColor: 'var(--streamlain-hover-color)',
  borderRadius: '4px',
  overflow: 'hidden',
  position: 'relative',
};

// sl-m82f.3.1: Sub-bar layered on top of the main fill to highlight one
// context (AI or Workflow) within the overall used range.
const subBarStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  borderRadius: '4px',
  transition: 'width 0.3s ease',
};

const breakdownRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
  marginTop: '6px',
  fontSize: '12px',
  color: 'var(--streamlain-text-secondary-color)',
};

const breakdownItemStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
};

const breakdownDotStyle = (color: string): CSSProperties => ({
  display: 'inline-block',
  width: '8px',
  height: '8px',
  borderRadius: '2px',
  backgroundColor: color,
});

const footerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '8px',
  fontSize: '12px',
  color: 'var(--streamlain-text-secondary-color)',
};

const warningStyle: CSSProperties = {
  marginTop: '10px',
  padding: '8px 12px',
  backgroundColor:
    'var(--streamlain-background-warning-color, rgba(255, 152, 0, 0.15))',
  border: '1px solid var(--streamlain-warning-color, #FF9800)',
  borderRadius: '6px',
  fontSize: '13px',
  color: 'var(--streamlain-warning-color, #FF9800)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
};

const buyMoreLinkStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--streamlain-primary-color)',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 600,
  padding: 0,
  textDecoration: 'underline',
  whiteSpace: 'nowrap',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const LOW_BALANCE_THRESHOLD = 0.2;

function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatMonthLabel(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

function fmt(value: number): string {
  if (!Number.isFinite(value)) return '0.00';
  return value.toFixed(2);
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * sl-m82f.3.1: which context to visually highlight inside the main fill.
 * - 'ai'      → highlight AI-context spend (default for workspace:ai tab)
 * - 'workflow' → highlight Workflow-context spend (default for workspace:workflow)
 * - 'total'   → no sub-bar, identical to the original sl-opil.7.2 widget
 */
export type CreditBalanceHighlight = 'ai' | 'workflow' | 'total';

export const CreditBalanceWidget = ({
  onNavigateToPlans,
  highlightContext = 'total',
}: {
  onNavigateToPlans?: () => void;
  /** sl-m82f.3.1 */
  highlightContext?: CreditBalanceHighlight;
} = {}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const workspaceId = workspace.id;

  // Managed AI is the only mode — trigger lazy-init but don't gate on enabled flag.
  const { isLoading: statusLoading } = useManagedAIStatus(
    workspaceId,
    workspace.flavour
  );
  const { balance, isLoading: balanceLoading } = useCreditBalance(
    workspaceId,
    workspace.flavour
  );

  const isLoading = statusLoading || balanceLoading;

  const usagePercent = useMemo(() => {
    if (!balance) return 0;
    const total = balance.displayMonthlyGrant;
    if (total <= 0) return 0;
    return Math.min((balance.displayMonthlyUsed / total) * 100, 100);
  }, [balance]);

  // sl-m82f.3.1: percent of the total grant that the highlighted context spent.
  const highlightPercent = useMemo(() => {
    if (!balance || highlightContext === 'total') return 0;
    const total = balance.displayMonthlyGrant;
    if (total <= 0) return 0;
    const highlighted =
      highlightContext === 'ai'
        ? (balance.displayAiUsed ?? 0)
        : (balance.displayWorkflowUsed ?? 0);
    return Math.min((highlighted / total) * 100, 100);
  }, [balance, highlightContext]);

  const isLowBalance = 100 - usagePercent < LOW_BALANCE_THRESHOLD * 100;

  if (isLoading || !balance) {
    return null;
  }

  const progressFillStyle: CSSProperties = {
    height: '100%',
    width: `${usagePercent}%`,
    backgroundColor: isLowBalance
      ? 'var(--streamlain-warning-color, #f59e0b)'
      : 'var(--streamlain-text-secondary-color)',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  };

  // sl-m82f.3.1: highlighted sub-bar is rendered on top of the main fill,
  // using the primary brand color so it stands out from the secondary fill.
  const subBarFillStyle: CSSProperties = {
    ...subBarStyle,
    width: `${highlightPercent}%`,
    backgroundColor: 'var(--streamlain-primary-color)',
  };

  // sl-m82f.3.1: defensive read for tenants that haven't migrated yet.
  const aiUsed = balance.displayAiUsed ?? 0;
  const workflowUsed = balance.displayWorkflowUsed ?? 0;
  const otherUsed = balance.displayOtherUsed ?? 0;
  const titleLabel = formatMonthLabel(new Date(balance.periodStart));
  const title =
    t['com.streamlain.settings.workspace.ai.credits.title']?.() ??
    'Credit Usage';

  return (
    <SettingWrapper title={`${title} · ${titleLabel}`}>
      <div
        style={containerStyle}
        data-testid="credit-balance-widget"
        data-highlight-context={highlightContext}
      >
        {/* Main: X / Y verbraucht */}
        <div style={headerRowStyle}>
          <p style={usageTextStyle} data-testid="credit-balance-display">
            {fmt(balance.displayMonthlyUsed)}
            <span style={totalTextStyle}>
              {' '}
              / {fmt(balance.displayMonthlyGrant)}
            </span>
          </p>
        </div>

        {/* Progress bar — fills as credits are consumed */}
        <div style={progressBarBgStyle}>
          <div style={progressFillStyle} />
          {/* sl-m82f.3.1: sub-bar overlay highlighting AI or Workflow */}
          {highlightContext !== 'total' && highlightPercent > 0 && (
            <div style={subBarFillStyle} data-testid="credit-balance-sub-bar" />
          )}
        </div>

        {/* sl-m82f.3.1: explicit AI / Workflow / Other breakdown */}
        <div style={breakdownRowStyle} data-testid="credit-balance-breakdown">
          <span
            style={breakdownItemStyle}
            data-testid="credit-balance-breakdown-ai"
          >
            <span
              style={breakdownDotStyle(
                highlightContext === 'ai'
                  ? 'var(--streamlain-primary-color)'
                  : 'var(--streamlain-text-secondary-color)'
              )}
            />
            {t['com.streamlain.settings.workspace.ai.credits.breakdown.ai']?.({
              amount: fmt(aiUsed),
            }) ?? `AI: ${fmt(aiUsed)}`}
          </span>
          <span
            style={breakdownItemStyle}
            data-testid="credit-balance-breakdown-workflow"
          >
            <span
              style={breakdownDotStyle(
                highlightContext === 'workflow'
                  ? 'var(--streamlain-primary-color)'
                  : 'var(--streamlain-text-secondary-color)'
              )}
            />
            {t[
              'com.streamlain.settings.workspace.ai.credits.breakdown.workflow'
            ]?.({ amount: fmt(workflowUsed) }) ??
              `Workflow: ${fmt(workflowUsed)}`}
          </span>
          {otherUsed > 0 && (
            <span
              style={breakdownItemStyle}
              data-testid="credit-balance-breakdown-other"
            >
              <span
                style={breakdownDotStyle(
                  'var(--streamlain-text-secondary-color)'
                )}
              />
              {t[
                'com.streamlain.settings.workspace.ai.credits.breakdown.other'
              ]?.({ amount: fmt(otherUsed) }) ?? `Other: ${fmt(otherUsed)}`}
            </span>
          )}
        </div>

        {/* Footer: period dates */}
        <div style={footerStyle}>
          <span>
            {t['com.streamlain.settings.workspace.ai.credits.period']?.() ??
              'Period'}
            : {formatDate(new Date(balance.periodStart))} –{' '}
            {formatDate(new Date(balance.periodEnd))}
          </span>
          <span>{Math.round(usagePercent)}% used</span>
        </div>

        {/* Low balance warning with buy-more link */}
        {isLowBalance && (
          <div style={warningStyle}>
            <span>
              {t[
                'com.streamlain.settings.workspace.ai.credits.low-balance-warning'
              ]?.({
                percent: String(Math.round(LOW_BALANCE_THRESHOLD * 100)),
              }) ??
                `Less than ${Math.round(LOW_BALANCE_THRESHOLD * 100)}% of your monthly credits remaining.`}
            </span>
            {onNavigateToPlans && (
              <button
                type="button"
                onClick={onNavigateToPlans}
                style={buyMoreLinkStyle}
              >
                {t[
                  'com.streamlain.settings.workspace.ai.credits.buy-more'
                ]?.() ?? 'Buy more credits'}
              </button>
            )}
          </div>
        )}
      </div>
      {onNavigateToPlans && (
        <SettingRow
          style={{ marginTop: '12px' }}
          name={
            t['com.streamlain.payment.view-plans-hint']?.() ??
            'Manage add-ons and upgrade your plan in the Plans tab.'
          }
          desc=""
        >
          <Button variant="primary" onClick={onNavigateToPlans}>
            {t['com.streamlain.payment.view-plans']?.() ?? 'View plans'}
          </Button>
        </SettingRow>
      )}
    </SettingWrapper>
  );
};
